/**
 * Quem executa a ação oferecida, por NAMESPACE de store ([T015] / US-4).
 *
 * O cartão `plataforma/acao-oferecida` vive dentro do thread e, no clique,
 * precisa (a) executar o handler da tool de front e (b) devolver o
 * `tool_output` pela MESMA instância de `APIChat` que abriu o turno. São três
 * instâncias em produção — `aawz-intelligence-v2` (chat do CRM), `feeFixoIa`
 * (guia e drawer do Fee Fixo) e `aiFramework` — e importar o singleton
 * `apiChat` dentro do cartão faria o drawer do Fee Fixo responder pelo chat do
 * CRM: o `tool_output` iria para outra conversa e o turno do Fee Fixo ficaria
 * pendurado esperando para sempre.
 *
 * O namespace viaja nos props do cartão (o parser o escreve ao montar a parte),
 * porque é um dado — string —, e não uma função guardada em estado reativo.
 *
 * ★ Dois botões do thread passam por aqui: o "Abrir" do cartão de ação oferecida
 *   e o "Tentar de novo" da bolha de erro do turno (`reenviarUltimoTurno`, do
 *   G-2 da VERIFY it.2). O motivo é o mesmo nos dois — quem responde pelo turno
 *   é a instância daquela superfície, não o singleton do chat do CRM.
 *
 * ★ Mapa em escopo de MÓDULO é a exceção consciente à regra "estado de stream
 *   mora na instância" (§12 do CLAUDE.md do módulo): o que mora aqui não é
 *   estado de turno, é o índice de QUEM atende cada namespace. A chave é o
 *   namespace justamente para que duas superfícies abertas não se atropelem.
 */

const EXECUTORES = {}

/**
 * @param {string} namespace  Namespace Vuex da superfície.
 * @param {Object} executor   Objeto com `executarAcaoOferecida({toolCallId})`.
 * @returns {Function} desfaz o registro (útil no `beforeDestroy` e no teste).
 */
export function registrarExecutorDeAcao(namespace, executor) {
  if (!namespace || !executor) return () => {}
  // A instância mais recente vence: uma view remontada (o `RouterView` é keyado
  // por `fullPath`) registra de novo, e a anterior já não tem stream.
  EXECUTORES[namespace] = executor
  return () => esquecerExecutorDeAcao(namespace, executor)
}

/**
 * Só apaga se o executor ainda for AQUELE. Sem esta guarda, o `beforeDestroy`
 * da view antiga apagaria o registro que a view nova acabou de fazer — e o
 * clique no cartão cairia no vazio depois de qualquer navegação.
 */
export function esquecerExecutorDeAcao(namespace, executor) {
  if (!namespace) return
  if (executor && EXECUTORES[namespace] !== executor) return
  delete EXECUTORES[namespace]
}

export function executorDeAcao(namespace) {
  if (!namespace) return null
  return EXECUTORES[namespace] || null
}

/** Esvazia o índice. Existe para os testes. */
export function limparExecutoresDeAcao() {
  Object.keys(EXECUTORES).forEach((chave) => {
    delete EXECUTORES[chave]
  })
}

/**
 * Executa a ação oferecida pela superfície dona do turno.
 *
 * Sem executor registrado, AVISA e devolve `null`: o clique não pode estourar
 * uma exceção no meio da conversa, e o console é onde o desenvolvedor descobre
 * que a superfície esqueceu de se registrar.
 *
 * @returns {Promise<?Object>} o `output` da tool, ou `null`.
 */
export function reenviarUltimoTurno(namespace) {
  const executor = executorDeAcao(namespace)
  if (!executor || typeof executor.reenviarUltimoTurno !== 'function') {
    console.warn(
      '[UI_REGISTRY] sem executor de ação para o namespace "' + namespace +
        '": não há motor de chat para reenviar o turno.'
    )
    return Promise.resolve(null)
  }
  return Promise.resolve(executor.reenviarUltimoTurno())
}

export function executarAcaoOferecida(namespace, payload) {
  const executor = executorDeAcao(namespace)
  if (!executor || typeof executor.executarAcaoOferecida !== 'function') {
    console.warn(
      '[UI_REGISTRY] sem executor de ação para o namespace "' + namespace +
        '": a superfície não registrou o motor de chat.'
    )
    return Promise.resolve(null)
  }
  return Promise.resolve(executor.executarAcaoOferecida(payload))
}
